/**
 * URLs dos portais e identidade do portal atual.
 *
 * Até a #996 este arquivo carregava três listas literais de produção: a URL de produção
 * de cada portal, os domínios-base conhecidos e um mapa `hostname -> app` com dez hosts
 * cravados. Um bundle de staging as levava junto, e era por elas que ele conseguia
 * navegar para produção. Agora tudo isto sai do contrato de deploy — as variáveis
 * obrigatórias do build (`config/deploymentContract.ts`).
 *
 * O contrato entra por PARÂMETRO, com o default lendo o contrato ativo, para que a
 * régua continue testável sem montar `import.meta.env`.
 */

import { isLocalhostHostname } from '../utils/devHost'
import { getDeploymentContract } from './activeDeploymentContract'
import type { Application, DeploymentContract } from './deploymentContract'

export type OrganizationType = 'system' | 'cloud_provider' | 'reseller' | 'end_customer'

/**
 * Os cinco portais. É o mesmo conjunto de `Application` (o `VITE_APP_TYPE` do
 * contrato): antes desta issue o tipo tinha só quatro valores e o `hub` era um caso
 * solto que cada arquivo tratava por conta — `store/auth.ts` conhecia `'hub'` e
 * `'legacy'`, `config/apps.ts` não conhecia nenhum dos dois.
 */
export type AppIdentifier = Application

export interface AppConfig {
  type: OrganizationType
  identifier: Exclude<AppIdentifier, 'hub'>
  name: string
  color: string
  urls: {
    /** Porta local do portal. A URL de produção vem do contrato, não daqui. */
    development: string
  }
}

export const APP_CONFIGS: Record<OrganizationType, AppConfig> = {
  system: {
    type: 'system',
    identifier: 'adm',
    name: 'Admin Portal',
    color: 'red',
    urls: {
      development: 'http://localhost:3001',
    },
  },
  cloud_provider: {
    type: 'cloud_provider',
    identifier: 'cp',
    name: 'Cloud Provider Portal',
    color: 'blue',
    urls: {
      development: 'http://localhost:3002',
    },
  },
  reseller: {
    type: 'reseller',
    identifier: 'rs',
    name: 'Reseller Portal',
    color: 'green',
    urls: {
      development: 'http://localhost:3003',
    },
  },
  end_customer: {
    type: 'end_customer',
    identifier: 'ec',
    name: 'End Customer Portal',
    color: 'purple',
    urls: {
      development: 'http://localhost:3004',
    },
  },
}

const SHORT_SUBDOMAIN_BY_ORG_TYPE: Record<OrganizationType, string> = {
  system: 'adm',
  cloud_provider: 'cp',
  reseller: 'rs',
  end_customer: 'ec',
}

const LONG_SUBDOMAIN_BY_ORG_TYPE: Record<OrganizationType, string> = {
  system: 'admin',
  cloud_provider: 'cp',
  reseller: 'reseller',
  end_customer: 'customer',
}

/**
 * O subdomínio usado quando o host atual não indica estilo: fora dos domínios da
 * plataforma (o portal white-label de uma revenda em domínio próprio) ou sob um deles
 * com prefixo que não é de portal (o subdomínio próprio de uma revenda na zona, #1106).
 *
 * É misto (`admin` para o adm, curto para os outros) porque é o que o DNS publicado tem
 * hoje, e era o que os literais `urls.production` diziam antes desta issue. Ambiente que
 * não siga esse desenho declara `VITE_PORTAL_URL_<APP>` e não depende deste mapa.
 */
const PUBLISHED_SUBDOMAIN_BY_ORG_TYPE: Record<OrganizationType, string> = {
  system: 'admin',
  cloud_provider: 'cp',
  reseller: 'rs',
  end_customer: 'ec',
}

const APP_IDENTIFIER_BY_ORG_TYPE: Record<OrganizationType, AppIdentifier> = {
  system: 'adm',
  cloud_provider: 'cp',
  reseller: 'rs',
  end_customer: 'ec',
}

const getBaseDomain = (hostname: string, baseDomains: readonly string[]): string | null => {
  const host = hostname.toLowerCase()
  for (const baseDomain of baseDomains) {
    if (host === baseDomain || host.endsWith(`.${baseDomain}`)) {
      return baseDomain
    }
  }

  return null
}

/**
 * Qual mapa de subdomínios o host atual indica, sob um domínio-base da plataforma.
 *
 * O prefixo só diz o estilo quando É o de um portal: `adm`/`rs`/`ec` são curtos,
 * `admin`/`reseller`/`customer` são longos (`cp` é igual nos dois). Qualquer outro
 * prefixo — o subdomínio próprio de uma revenda na zona da plataforma, como
 * `netwiseempresas.<zona>` — não diz estilo nenhum, e o destino é o subdomínio
 * PUBLICADO. Era o mapa longo antes da #1106, e a troca EC -> RS abria `reseller.<zona>`.
 */
const subdomainMapForHost = (
  hostname: string,
  baseDomain: string,
): Record<OrganizationType, string> => {
  const host = hostname.toLowerCase()
  const prefix = host === baseDomain ? '' : host.slice(0, -(baseDomain.length + 1))
  if (prefix === 'adm' || prefix === 'cp' || prefix === 'rs' || prefix === 'ec') {
    return SHORT_SUBDOMAIN_BY_ORG_TYPE
  }
  if (prefix === 'admin' || prefix === 'reseller' || prefix === 'customer') {
    return LONG_SUBDOMAIN_BY_ORG_TYPE
  }
  return PUBLISHED_SUBDOMAIN_BY_ORG_TYPE
}

export const getAppIdentifierByOrgType = (orgType: OrganizationType): AppIdentifier =>
  APP_IDENTIFIER_BY_ORG_TYPE[orgType]

/**
 * URL do portal que atende um tipo de organização.
 *
 * Ordem: localhost -> override explícito do ambiente -> domínio-base que casa com o
 * host atual -> primeiro domínio-base declarado. Nenhum ramo termina em literal.
 */
export const getFrontendUrlByOrgType = (
  orgType: OrganizationType,
  contract: DeploymentContract = getDeploymentContract(),
): string => {
  const { hostname, protocol } = window.location
  const config = APP_CONFIGS[orgType] ?? APP_CONFIGS.end_customer

  if (!APP_CONFIGS[orgType]) {
    console.warn(`Unknown organization type: ${orgType}, defaulting to end_customer`)
  }

  // `<slug>.localhost` conta como dev (#737): sem isso, o dev do portal white-label
  // local era mandado para o portal de PRODUÇÃO.
  if (isLocalhostHostname(hostname)) {
    return config.urls.development
  }

  const override = contract.portalUrls[config.identifier]
  if (override) {
    return override
  }

  const baseDomain = getBaseDomain(hostname, contract.portalBaseDomains)
  if (baseDomain) {
    return `${protocol}//${subdomainMapForHost(hostname, baseDomain)[config.type]}.${baseDomain}`
  }

  // Host fora dos domínios da plataforma: o portal white-label de uma revenda em
  // domínio próprio. O destino é o domínio-base CANÔNICO — o primeiro declarado.
  return `${protocol}//${PUBLISHED_SUBDOMAIN_BY_ORG_TYPE[config.type]}.${contract.portalBaseDomains[0]}`
}

/**
 * Qual portal este bundle é.
 *
 * Vem do contrato do build, e não mais de um mapa `hostname -> app`: o bundle SABE o
 * que é (foi buildado como tal), enquanto o mapa precisava adivinhar pelo host — errava
 * em todo host que não estivesse na lista (domínio próprio de revenda caía em
 * `'unknown'` no `store/auth.ts`) e carregava dez hosts de produção para dentro de todo
 * bundle, inclusive os de staging.
 */
export const getCurrentAppIdentifier = (
  contract: DeploymentContract = getDeploymentContract(),
): AppIdentifier => contract.application

/**
 * Get app config by identifier
 */
export const getAppConfigByIdentifier = (identifier: AppIdentifier): AppConfig | undefined => {
  return Object.values(APP_CONFIGS).find((config) => config.identifier === identifier)
}
